import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { BeeLogo } from "./BeeLogo";
import { GlobalFooter } from "./GlobalFooter";
import { Moon, Sun, Settings as SettingsIcon, LogOut, Globe } from "lucide-react";
import t from "../lib/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export const Layout = ({ children, hideNav = false }) => {
  const { parent, language, setLanguage, settings, updateSettings, logout } = useApp();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bn-honeycomb" data-testid="app-shell">
      <a href="#bn-main" className="bn-skip-link" data-testid="skip-to-main">
        {language === "es" ? "Saltar al contenido" : "Skip to content"}
      </a>
      {!hideNav && (
        <header
          role="banner"
          className="sticky top-0 z-30 backdrop-blur-xl"
          style={{ background: "color-mix(in srgb, var(--bn-bg) 78%, transparent)" }}
        >
          <nav
            aria-label={language === "es" ? "Navegación principal" : "Main navigation"}
            className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between"
          >
            <Link to={parent ? "/dashboard" : "/"} className="flex items-center gap-3" data-testid="nav-home-link">
              <BeeLogo size={36} variant="portrait" />
              <span className="display text-xl md:text-2xl font-semibold">BeeNest</span>
            </Link>
            <div className="flex items-center gap-2">
              <button
                onClick={() => updateSettings({ bedtime_mode: !settings.bedtime_mode })}
                className="bn-pill text-sm flex items-center gap-2 hover:bg-[var(--bn-surface)]"
                style={{ background: "var(--bn-surface)" }}
                data-testid="toggle-bedtime-mode"
                aria-label={t(language, "toggleBedtime")}
                aria-pressed={settings.bedtime_mode}
              >
                {settings.bedtime_mode ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
                <span className="hidden sm:inline">{settings.bedtime_mode ? t(language, "day") : t(language, "night")}</span>
              </button>
              {parent && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="bn-pill text-sm flex items-center gap-2"
                      style={{ background: "var(--bn-surface)" }}
                      data-testid="nav-menu-trigger"
                      aria-label={t(language, "languageMenu")}
                    >
                      <Globe size={16} aria-hidden="true" />
                      <span className="hidden sm:inline">{language === "es" ? "ES" : "EN"}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-2xl">
                    <DropdownMenuLabel>{t(language, "language")}</DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => setLanguage("en")} data-testid="lang-en">
                      English
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setLanguage("es")} data-testid="lang-es">
                      Español
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="nav-settings">
                      <SettingsIcon size={16} className="mr-2" aria-hidden="true" />
                      {t(language, "settings")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/membership")} data-testid="nav-membership">
                      {t(language, "membership")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/letters")} data-testid="nav-letters">
                      {t(language, "familyLetters")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout">
                      <LogOut size={16} className="mr-2" aria-hidden="true" />
                      {t(language, "logout")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </nav>
        </header>
      )}
      <main
        id="bn-main"
        role="main"
        tabIndex={-1}
        className="max-w-6xl mx-auto px-6 md:px-10 py-10 bn-fade focus:outline-none"
      >
        {children}
      </main>
      <GlobalFooter />
    </div>
  );
};

export default Layout;
